import { useEffect, useState } from 'react'
import { api, can, readIf, type Assessment, type Capability, type Me, type Vendor } from '../lib/api'
import { onScreen } from '../lib/screens'
import { Icon } from './ui'
import '../styles/setup-guide.css'

// The setup guide on Home for a workspace that is just starting (release 3.2,
// dashboard spec card H).
//
// # WHAT IT IS
//
// Four steps, the order a programme actually starts in, each ticked by what the
// workspace holds rather than by a press: a vendor on the register, a
// questionnaire sent, answers scored, a vendor approved into the register. It
// is not a tour: every step is one link to the screen where it is done, and
// the guide says nothing once the steps are done. It can be dismissed, which
// is remembered in this browser for this workspace.
//
// # WHO SEES IT, AND WHEN IT READS
//
// Somebody who runs the programme: a reader who may send a questionnaire, and
// of the four steps only those their roles can do (review of PR 24, M7). A
// business owner, a requester or procurement may add a vendor but run no
// programme, and is not shown a guide of steps for somebody else. Only in a
// workspace that is starting: fewer than NEW_WORKSPACE vendors on the
// register. The steps are
// ticked from two list reads, which in a register of hundreds would be two
// heavy reads on every visit to Home for a guide that has nothing to say; a
// guide that has been completed once is remembered and never reads again.

export type GuideStep = { key: string; text: string; done: boolean; href: string; act: string }

// What a reader needs to do each step.
const STEP_NEEDS: Record<string, Capability> = {
  vendor: 'vendor.create',
  send: 'questionnaire.issue',
  score: 'assessment.score',
  approve: 'vendor.approve',
}

// Whether the guide is for this reader at all: somebody who runs the programme.
export const GUIDE_NEEDS: Capability = 'questionnaire.issue'

// A questionnaire counts as sent once it left draft: a draft is still being
// written and the vendor has nothing.
const NOT_SENT = new Set(['DRAFT', 'CANCELLED'])

export function guideSteps(vendors: Vendor[], assessments: Assessment[], may: (capability: Capability) => boolean = can): GuideStep[] {
  const all: GuideStep[] = [
    { key: 'vendor', text: 'Add your first vendor', done: vendors.length > 0, href: '#/vendor/new', act: 'Add a vendor' },
    {
      key: 'send',
      text: 'Send it a questionnaire',
      done: assessments.some((a) => !NOT_SENT.has(a.status)),
      href: vendors[0] ? '#/vendor/' + vendors[0].id : '#/vendors',
      act: 'Open the vendor',
    },
    {
      key: 'score',
      text: 'Review and score the answers',
      done: assessments.some((a) => a.status === 'SCORED' || a.status === 'ACCEPTED') || vendors.some((v) => Boolean(v.currentBand)),
      href: '#/my-work?show=review',
      act: 'Open reviews',
    },
    {
      key: 'approve',
      text: 'Approve a vendor into the register',
      done: vendors.some((v) => v.status === 'ACTIVE' || v.status === 'SUSPENDED' || v.status === 'OFFBOARDING' || v.status === 'OFFBOARDED'),
      href: '#/my-work?show=decide',
      act: 'Open decisions',
    },
  ]
  return all.filter((s) => {
    const need = STEP_NEEDS[s.key]
    return need !== undefined && may(need)
  })
}

const NEW_WORKSPACE = 10

// The roles allow it and it is part of the reader's job: adding a vendor is
// offered only where the persona has the Add a vendor screen, as the sidebar
// offers it.
function mayDo(capability: Capability): boolean {
  return can(capability) && (capability !== 'vendor.create' || onScreen('vendor-add'))
}

function dismissKey(me: Me): string {
  return 'tprm.setup.dismissed.' + (me.tenant?.id ?? '')
}

// Dismissed by the reader, or finished: either way the guide is done here.
function wasDismissed(me: Me): boolean {
  try {
    return localStorage.getItem(dismissKey(me)) === '1'
  } catch {
    return false
  }
}

function remember(me: Me) {
  try {
    localStorage.setItem(dismissKey(me), '1')
  } catch {
    // A browser that refuses storage asks again next visit.
  }
}

export function SetupGuideView({ steps, onDismiss }: { steps: GuideStep[]; onDismiss: () => void }) {
  const done = steps.filter((s) => s.done).length
  const next = steps.find((s) => !s.done)
  return (
    <section className="hq-guide" aria-labelledby="hq-guide-h">
      <div className="hq-guide-head">
        <h2 id="hq-guide-h">Set up your programme</h2>
        <span className="hq-quiet">
          {done} of {steps.length} done
        </span>
        <button type="button" className="ghost" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
      <ol className="hq-steps">
        {steps.map((s) => (
          <li key={s.key} className={s.done ? 'done' : s === next ? 'next' : ''}>
            <span className="hq-tick" aria-hidden="true">
              {s.done ? <Icon name="check" size={14} /> : null}
            </span>
            <span className="hq-step-text">
              {s.text}
              <span className="vh">{s.done ? ', done' : ', not done yet'}</span>
            </span>
            {s === next && (
              <a className="button primary" href={s.href}>
                {s.act}
              </a>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}

export function SetupGuide({ me, vendorCount }: { me: Me; vendorCount?: number }) {
  const [dismissed, setDismissed] = useState(() => wasDismissed(me))
  const [steps, setSteps] = useState<GuideStep[] | null>(null)
  const eligible = can(GUIDE_NEEDS) && !dismissed && vendorCount !== undefined && vendorCount < NEW_WORKSPACE

  useEffect(() => {
    if (!eligible) return
    let live = true
    // Two reads, made only while the guide can still show: once every step is
    // done it stops asking, and a dismissed guide never asks.
    Promise.all([api.vendors({}), readIf('assessment.read', () => api.assessments(), { assessments: [] as Assessment[] })])
      .then(([v, a]) => {
        if (!live) return
        const got = guideSteps(v.vendors ?? [], a.assessments ?? [], mayDo)
        // Finished once is finished: it does not read again on later visits.
        if (got.every((s) => s.done)) remember(me)
        setSteps(got)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
    // me is the signed-in reader, the same object for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, vendorCount])

  if (!eligible || !steps || steps.length === 0 || steps.every((s) => s.done)) return null
  return (
    <SetupGuideView
      steps={steps}
      onDismiss={() => {
        setDismissed(true)
        remember(me)
      }}
    />
  )
}
